import React, { useEffect } from 'react';
import { NotificationBanner } from '../NotificationBanner/NotificationBanner';
import type { NotificationBannerType } from '../NotificationBanner/NotificationBanner';
import { CtaButton, CtaButtonColorVariant, CtaButtonSize } from '../CtaButton/CtaButton';
import type { CtaBehavior } from '../../utils/ctaBehavior';
import type { CtaAction } from '../../utils/ctaAction';
import { StoreBadgeRow, STORE_BADGE_NAMES } from '../StoreBadgeLink/StoreBadgeLink';
import { Image } from '../Image/Image';
import type { ImageAlignment, OverlayPosition } from '../Image/Image';
import { Video } from '../Video/Video';
import ReactMarkdown from 'react-markdown';
import './Hero.scss';

/**
 * Hero - Standard Hero component for Poly CMS
 *
 * Layout variants:
 * - `primary`: XL H1, large CTA, side image
 * - `secondary`: L H1, eyebrow, medium CTA, often dual CTA, side image
 * - `centered`: Centered text, full-width image below content
 * - `headlineOnly`: Centered headline only, no subheading/buttons/image
 *
 * `incentivesText` (Markdown supported) renders on its own line below the CTA group.
 * `alertText` (all variants except headlineOnly) renders a banner above the content; `alertType` picks its treatment.
 */

export const HERO_VARIANTS = ['primary', 'secondary', 'centered', 'headlineOnly'] as const;
export type HeroVariant = (typeof HERO_VARIANTS)[number];

export const HERO_BACKGROUNDS = ['white', 'off-white'] as const;
export type HeroBackground = (typeof HERO_BACKGROUNDS)[number];

/**
 * @polySemanticUsage About this block: the primary above-the-fold section of a page. Designed to
 * immediately communicate the page's core value proposition with a headline, supporting copy,
 * primary CTA, and optional media (image or video). One per page; sets the narrative tone.
 * Variant selection — evaluate the source hero in this order and take the first match:
 * (1) centered (the "Special Feature Hero") when the hero is center-aligned AND features a
 * prominent full-width video with inline playback — both indicators together; this variant is
 * rare. (2) secondary when the hero has an eyebrow (category/program label above the headline)
 * OR its CTA is not a registration action (e.g. Learn more, Watch video, Explore FBA, Download
 * guide) — either indicator alone is sufficient; program pages and deep-dive education pages
 * land here. (3) primary when the page is high-level, high-traffic, and the CTA drives
 * registration (Sign up, Get started, Start selling with Amazon). (4) Still ambiguous: default
 * to secondary and record the ambiguity in your migration self-evaluation notes — never as
 * visible text in any content field. Eyebrow only renders when variant is secondary; populating
 * eyebrow on any other variant silently drops it. Eyebrow content: the eyebrow is the
 * page's own category/program label. Breadcrumb trails are navigation, not eyebrows — never
 * migrate a breadcrumb into eyebrow. If the source's only label-like element is a breadcrumb,
 * use the page's own (deepest) crumb, never an ancestor section name (for a page titled "Build
 * an online store" under a Blog section, "Blog" is wrong — the page's own label is right).
 * Background: ALWAYS set background explicitly — the schema default is off-white, so
 * leaving the field unset renders off-white. Match the source page's hero background exactly:
 * a white source hero migrates with background white, never off-white. Choose off-white only
 * when the source hero visibly uses the off-white tone; when in doubt, set white.
 * Choose CTA button colors by action type, not by appearance. When the primary
 * action is registration (Sign up, Start selling, Get started), set primaryCtaColorVariant to
 * primary (Smile Orange fill). When the primary action is not registration (e.g. Enroll your
 * brand — the "dark" CTA treatment), set primaryCtaColorVariant to contrast (dark fill, white
 * text). Secondary actions (Learn more, Watch overview) take secondary (outlined: transparent
 * background with a dark border), typically on secondaryCtaColorVariant. tertiary renders a plain
 * text link and is not part of this mapping.
 * Dual-CTA rule: never migrate a decision-tree CTA pair — two buttons that segment
 * users by account status, e.g. "Sign up" + "Log in to get started" — as two buttons. Migrate
 * only the registration action as the primary CTA and move the existing-seller login action into
 * the below-CTA text (incentivesText/secondaryIncentivesText). A dual CTA is acceptable only when
 * the secondary button is a discovery action for the same prospective-seller audience, e.g.
 * "Start selling" + "Watch overview" or "Start selling" + "Learn more".
 * Subheading: subheading is plain text only. If the source subheading contains a hyperlink, keep
 * the sentence text in subheading without the link, and put the link in the below-CTA text
 * (incentivesText, or secondaryIncentivesText if incentivesText is already used) as a markdown
 * link. The link is one more below-CTA candidate under the priority rule below and ranks last:
 * when both slots are already taken, or the hero has no CTA button or store badges, drop the
 * link and keep only the subheading text. Never add more than the primary and secondary CTAs
 * plus the below-CTA lines; never turn the link into a CTA button. Do not put lists in
 * subheading; keep list items as plain sentences.
 * Below-CTA text placement: incentivesText and secondaryIncentivesText each render
 * as a full-width line below the ENTIRE CTA button group — they are never attached to one
 * specific button. These two props are the only below-CTA slots; never place CTA-adjacent
 * text in any other field. Both slots render only when the hero has at least one CTA button
 * or store badges — a hero with neither silently drops them. Map source elements near the
 * CTA into them: pricing/fee
 * information, promotional/incentive text, and the demoted existing-seller login action.
 * When more than two qualify, priority depends on where pricing sat in the source: pricing/fee
 * clarification ranks FIRST only when it already sits directly adjacent to the CTA in the
 * source (right beside the button or directly below it) — keep it as the first below-CTA line.
 * Otherwise the priority is (1) promotional text,
 * (2) existing-seller login message, (3) pricing/fee clarification — drop the lowest-priority
 * items to stay within the two slots.
 * Asterisk footnote rule: when the CTA button text carries an asterisk (e.g.
 * "Sign up*"), the pricing/fee clarification migrates as a footnote instead — a RichText block
 * kept at its source position on the page (below the hero, mid-page, or at the page bottom,
 * wherever the source showed it) — and does not consume a below-CTA slot. Pricing that appeared
 * elsewhere on the page as a detached footnote likewise stays a separate RichText footnote at
 * its source position rather than being pulled under the CTA. Contextual text ABOVE the source
 * CTA that routes users by account status (e.g. "For new sellers" / "Already selling?") is
 * handled by page-level IA — remove it entirely; do not migrate it into any hero field.
 * For image overlays, give every overlay you populate an explicit overlay{N}Position and never
 * reuse a position: an overlay whose position is already taken by an earlier slot is silently
 * dropped. Each slot has its own distinct default (overlay1 bottom-right, overlay2 top-left),
 * so partial positioning is the trap — setting overlay1Position to top-left while leaving
 * overlay2Position unset collides with overlay2's top-left default and drops overlay2.
 * Position all overlays explicitly and uniquely, or set no positions at all. Leave
 * overlay{N}Width unset unless the source shows a specific size; when set, use pixels.
 * Image fit: use Fill for photography and Fit for transparent assets or images whose full
 * contents must remain visible. Image alignment selects the retained crop under Fill and the
 * placement within empty space under Fit. These settings apply to Primary, Secondary, and
 * image-only Centered heroes. A Centered hero with video keeps its poster at Fill and Center.
 * Link behavior: always leave primaryCtaBehavior / secondaryCtaBehavior unset (same tab), even
 * when the source link opens in a new tab or is an External Link type. Migration never sets
 * newTab; editors add it by hand for the few exceptions. Never infer it from the destination host.
 * Video: on primary and secondary, a source video (a play thumbnail or a "Watch" CTA that plays a
 * video) migrates to videoUrl, and the hero renders a "Watch overview" button (label in
 * videoCtaText) in the secondary CTA slot that opens the video in a modal. Any secondaryCta* fields
 * are then not shown, so never populate both; never put a video link in secondaryCtaHref. Always
 * set videoCtaText whenever videoUrl is set: use the source button's label, or "Watch overview"
 * when the source shows only a play thumbnail. The built-in fallback label is English only, so a
 * blank videoCtaText shows English text on translated pages. On centered, videoUrl plays in place
 * on the image below the text.
 */
export interface HeroProps {
  /**
   * Hero variant - determines styling and layout
   * @polyLabel Hero Variant
   * @polyHelp Choose the visual style for this hero section
   */
  variant?: HeroVariant;
  
  /** 
   * Background style preset
   * - `'off-white'`: Off-white background (default)
   * - `'white'`: White background
   * @polyHelp Background style for the section
   * @polyOptions White=white,Off-White=off-white
   * @polyDefault off-white
   */
  background?: HeroBackground;
  
  /**
   * Main heading text
   * @polyLabel Headline
   * @polyHelp Main headline text displayed prominently
   */
  heading: string;
  
  /**
   * Optional subheading text (plain text only; markdown is shown as literal characters).
   * Migration: a source subheading hyperlink moves to the below-CTA text, never here (see the
   * Subheading rule above).
   * @polyType Text
   * @polyLabel Subheadline
   * @polyHelp Supporting text below the headline
   */
  subheading?: string;

  /**
   * Optional eyebrow text. Renders only on the secondary variant — a source hero
   * with an eyebrow implies variant=secondary.
   * @polyHelp Small text above the headline. Only rendered on the Secondary variant.
   */
  eyebrow?: string;
  
  /**
   * Primary CTA button text
   * @polyLabel Primary CTA Text
   * @polyHelp Text for the primary call-to-action button
   */
  primaryCtaText?: string;
  
  /**
   * Primary CTA button href
   * @polyType Url
   * @polyLabel Primary CTA Link
   * @polyHelp URL the primary button navigates to
   */
  primaryCtaHref?: string;
  
  /**
   * Primary CTA button color variant
   * @polyType SelectList
   * @polyOptions Smile Orange=primary,Contrast=contrast,Outlined=secondary,Text Link=tertiary
   * @polyLabel Primary CTA Color
   */
  primaryCtaColorVariant?: Exclude<CtaButtonColorVariant, 'offWhite'>;
  
  /**
   * Whether the primary CTA opens in the current tab or a new one
   * @polyType SelectList
   * @polyOptions Same tab=sameTab,New tab=newTab
   * @polyHelp Same tab by default. Choose New tab only when the seller needs the destination open alongside this page (how-to articles), it is a download (PDF, app store), or leaving would break a flow or session. Sign up and Register always stay Same tab.
   * @polyDefault sameTab
   * @polyLabel Primary CTA Behavior
   * @polyTranslatable false
   */
  primaryCtaBehavior?: CtaBehavior;

  /**
   * What the primary CTA does.
   * @polyType SelectList
   * @polyOptions Link=link,Watch video=video,Download=download,Popup=popup
   * @polyHelp Popup opens the Popup block matching the Target Popup ID and does not use the CTA Link or Behavior fields.
   * @polyDefault link
   * @polyTranslatable false
   */
  primaryCtaAction?: CtaAction;

  /**
   * Popup ID to open when Primary CTA Action is Popup.
   * @polyHelp Match the Popup ID on the Popup block to open. Use A-Z, a-z, 0-9 and dashes; spaces become dashes, other characters are removed, and matching is case-sensitive.
   * @polyTranslatable false
   */
  primaryCtaTargetPopupId?: string;
  
  /**
   * Primary CTA button ID
   * @polyLabel Primary CTA ID
   * @polyTranslatable false
   */
  primaryCtaId?: string;
  
  /**
   * Primary CTA button ld-append for analytics
   * @polyLabel Primary CTA Analytics
   * @polyHelp data-ld-append attribute for analytics tracking
   * @polyTranslatable false
   */
  primaryCtaLdAppendix?: string;
  
  /**
   * Secondary CTA button text
   * @polyLabel Secondary CTA Text
   */
  secondaryCtaText?: string;
  
  /**
   * Secondary CTA button href
   * @polyType Url
   * @polyLabel Secondary CTA Link
   */
  secondaryCtaHref?: string;
  
  /**
   * Secondary CTA button color variant
   * @polyType SelectList
   * @polyOptions Smile Orange=primary,Contrast=contrast,Outlined=secondary,Text Link=tertiary
   * @polyLabel Secondary CTA Color
   */
  secondaryCtaColorVariant?: Exclude<CtaButtonColorVariant, 'offWhite'>;
  
  /**
   * Whether the secondary CTA opens in the current tab or a new one
   * @polyType SelectList
   * @polyOptions Same tab=sameTab,New tab=newTab
   * @polyHelp Same tab by default. Choose New tab only when the seller needs the destination open alongside this page (how-to articles), it is a download (PDF, app store), or leaving would break a flow or session. Sign up and Register always stay Same tab.
   * @polyDefault sameTab
   * @polyLabel Secondary CTA Behavior
   * @polyTranslatable false
   */
  secondaryCtaBehavior?: CtaBehavior;

  /**
   * What the secondary CTA does.
   * @polyType SelectList
   * @polyOptions Link=link,Watch video=video,Download=download,Popup=popup
   * @polyHelp Popup opens the Popup block matching the Target Popup ID and does not use the CTA Link or Behavior fields.
   * @polyDefault link
   * @polyTranslatable false
   */
  secondaryCtaAction?: CtaAction;

  /**
   * Popup ID to open when Secondary CTA Action is Popup.
   * @polyHelp Match the Popup ID on the Popup block to open. Use A-Z, a-z, 0-9 and dashes; spaces become dashes, other characters are removed, and matching is case-sensitive.
   * @polyTranslatable false
   */
  secondaryCtaTargetPopupId?: string;
  
  /**
   * Secondary CTA button ID
   * @polyLabel Secondary CTA ID
   * @polyTranslatable false
   */
  secondaryCtaId?: string;
  
  /**
   * Secondary CTA button ld-append for analytics
   * @polyLabel Secondary CTA Analytics
   * @polyHelp data-ld-append attribute for analytics tracking
   * @polyTranslatable false
   */
  secondaryCtaLdAppendix?: string;

  /**
   * Renders the official App Store and Google Play download badges in the CTA area.
   * Needs no text or link — the badges carry their own artwork and destinations. A hero
   * uses badges or button CTAs, not both.
   * @polyLabel Show App Store Badges
   * @polyHelp Adds the App Store and Google Play download badges. No other fields needed. Use instead of the CTA buttons, not alongside them.
   */
  storeBadges?: boolean;
  
  /**
   * First line of text rendered below the CTA button group (supports Markdown).
   * Spans the full content column below ALL CTA buttons — not attached to any single button.
   * @polyType RichText
   * @polyHelp First line of text below the whole CTA button group (pricing, promo, or login link) — not tied to a single button
   */
  incentivesText?: string;

  /**
   * Second line of text rendered below the first. Two lines maximum below the CTA group.
   * @polyType RichText
   * @polyHelp Optional second line of text below the CTA group (two lines maximum)
   */
  secondaryIncentivesText?: string;
  
  /**
   * Hero image source URL (on the centered variant, also the video poster when videoUrl is set)
   * @polyType Url
   * @polyLabel Image Source
   */
  imageSrc?: string;
  
  /**
   * Hero image alt text
   * @polyLabel Image Alt Text
   */
  imageAlt?: string;

  /**
   * How an image fills its media frame. Centered video posters keep their existing Fill behavior.
   * @polyDefault fill
   * @polyHelp Fill covers the whole image area and may crop the edges; use it for photography. Fit shows the whole image with transparent space around it; use it for transparent images or images that must not be cropped. Applies to Primary, Secondary, and Centered images, but not Centered video posters.
   * @polyTranslatable false
   */
  imageFit?: 'fill' | 'fit';

  /**
   * Where an image sits within its media frame. Centered video posters remain centered.
   * @polyDefault center
   * @polyHelp With Fill, picks which part of the image stays visible when it is cropped. With Fit, picks where the image sits in the image area. Applies to Primary, Secondary, and Centered images, but not Centered video posters.
   * @polyTranslatable false
   */
  imageAlignment?: ImageAlignment;

  /**
   * Video URL (YouTube or MP4). On primary and secondary heroes it adds a "Watch overview" video button in
   * the secondary CTA slot that opens the video in a modal; the secondary CTA is then not shown.
   * On the centered variant the bottom image becomes a click-to-play video that plays in place.
   * @polyType Url
   * @polyLabel Video URL
   * @polyHelp YouTube or MP4 link. Primary and Secondary: adds a "Watch overview" button (label set below) in place of the secondary CTA. Centered: plays in place on the image below the text.
   * @polyTranslatable false
   */
  videoUrl?: string;

  /**
   * Label for the "Watch overview" video button that primary and secondary heroes render when videoUrl is set.
   * @polyLabel Video Button Text
   * @polyHelp Label for the button that opens the video (Primary and Secondary only). Fill this in on every page so it gets translated. If blank, the button shows the English text "Watch overview".
   */
  videoCtaText?: string;

  /** Image position on tablet and desktop (for primary/secondary variants) */
  imagePosition?: 'left' | 'right';
  
  /** 
   * Mobile stacking behavior
   * - 'content-first': Content above image (default)
   * - 'image-first': Image above content
   * - 'maintain-order': Keep same order as desktop layout
   * @polyLabel Mobile Stacking
   * @polyHelp Controls how content and image stack on mobile
   * @polyOptions Content First=content-first,Image First=image-first,Maintain Order=maintain-order
   */
  mobileStackingMode?: 'content-first' | 'image-first' | 'maintain-order';
  
  // Overlay 1 props
  /**
   * @polyType Url
   * @polyLabel Overlay 1 Image URL
   * @polyHelp Image positioned on top of the main image
   */
  overlay1Url?: string;
  /**
   * @polyLabel Overlay 1 Alt Text
   */
  overlay1Alt?: string;
  /**
   * @polyType SelectList
   * @polyOptions Bottom Right=bottom-right,Bottom Left=bottom-left,Top Right=top-right,Top Left=top-left,Center=center
   */
  overlay1Position?: OverlayPosition;
  /**
   * @polyHelp Width in pixels, e.g. 240px. Leave blank to show the image at its designed size.
   * @polyTranslatable false
   */
  overlay1Width?: string;

  // Overlay 2 props
  /**
   * @polyType Url
   * @polyLabel Overlay 2 Image URL
   */
  overlay2Url?: string;
  /**
   * @polyLabel Overlay 2 Alt Text
   */
  overlay2Alt?: string;
  /**
   * @polyType SelectList
   * @polyOptions Bottom Right=bottom-right,Bottom Left=bottom-left,Top Right=top-right,Top Left=top-left,Center=center
   */
  overlay2Position?: OverlayPosition;
  /**
   * @polyHelp Width in pixels, e.g. 240px. Leave blank to show the image at its designed size.
   * @polyTranslatable false
   */
  overlay2Width?: string;

  /** Optional alert banner text. On primary/centered: above content. On secondary: inside content above eyebrow. */
  alertText?: string;

  /**
   * Treatment of the alert banner; same options as the Notification Banner block.
   * @polyType SelectList
   * @polyLabel Alert Type
   * @polyOptions Info=info,Tip=tip,Success=success,Warning=warning,Error=error
   * @polyDefault info
   */
  alertType?: NotificationBannerType;
}

/**
 * Maps background preset values to Poly design tokens
 */
const getBackgroundColor = (background?: HeroBackground): string => {
  const backgroundMap: Record<HeroBackground, string> = {
    'white': 'var(--poly-color-background-white)',
    'off-white': 'var(--poly-color-background-offWhite)',
  };
  
  if (!background) return backgroundMap['off-white'];
  
  const color = backgroundMap[background];
  if (!color) {
    console.warn(`[Hero] Unknown background preset: "${background}". Falling back to "off-white".`);
    return backgroundMap['off-white'];
  }
  
  return color;
};

/**
 * Returns the CTA button size based on variant.
 * Secondary uses 'medium' (48px). Primary, centered, and headlineOnly use 'default' (64px).
 */
const getCtaSize = (variant: HeroVariant): CtaButtonSize => {
  if (variant === 'secondary') return 'medium';
  return 'default';
};

export const Hero: React.FC<HeroProps> = ({
  variant = 'primary',
  background,
  heading,
  subheading,
  eyebrow,
  primaryCtaText,
  primaryCtaHref,
  primaryCtaColorVariant,
  primaryCtaId,
  primaryCtaLdAppendix,
  primaryCtaBehavior,
  primaryCtaAction,
  primaryCtaTargetPopupId,
  secondaryCtaText,
  secondaryCtaHref,
  secondaryCtaColorVariant,
  secondaryCtaId,
  secondaryCtaLdAppendix,
  secondaryCtaBehavior,
  secondaryCtaAction,
  secondaryCtaTargetPopupId,
  storeBadges,
  incentivesText,
  secondaryIncentivesText,
  imageSrc,
  imageAlt,
  imageFit,
  imageAlignment,
  videoUrl,
  videoCtaText,
  imagePosition = 'right',
  mobileStackingMode = 'content-first',
  overlay1Url,
  overlay1Alt,
  overlay1Position = 'bottom-right',
  overlay1Width,
  overlay2Url,
  overlay2Alt,
  overlay2Position = 'top-left',
  overlay2Width,
  alertText,
  alertType = 'info',
}) => {
  const HeadingTag = (variant === 'primary' || variant === 'centered' || variant === 'headlineOnly') ? 'h1' : 'h2';
  const ctaSize = getCtaSize(variant);
  const resolvedBackgroundColor = getBackgroundColor(background);
  const containerStyle = { backgroundColor: resolvedBackgroundColor };
  const hasVideoCta = !!videoUrl && (variant === 'primary' || variant === 'secondary');
  const imageObjectFit = imageFit === 'fit' ? 'contain' : 'cover';

  useEffect(() => {
    if (process.env.NODE_ENV !== 'production' && hasVideoCta && secondaryCtaText) {
      // eslint-disable-next-line no-console -- dev-only authoring feedback; stripped from production builds
      console.warn('[Hero] videoUrl and secondaryCtaText are both set. The Watch video button takes the secondary CTA slot, so the secondary CTA is not shown.');
    }
  }, [hasVideoCta, secondaryCtaText]);

  // The alert is the library's NotificationBanner so Hero and standalone banners share
  // one treatment; the wrapper is a positioning hook that Hero.scss targets by class.
  const alertBanner = alertText ? (
    <div className="hero__alert">
      <NotificationBanner type={alertType} description={alertText} />
    </div>
  ) : null;

  // Shared overlay props for Image component
  const overlayProps = {
    overlay1Url, overlay1Alt, overlay1Position, overlay1Width,
    overlay2Url, overlay2Alt, overlay2Position, overlay2Width,
  };

  // ============================================
  // HEADLINE ONLY: Just a centered heading
  // ============================================
  if (variant === 'headlineOnly') {
    return (
      <section className={`hero hero--headlineOnly`} style={containerStyle}>
        <div className="hero__inner hero__inner--centered">
          <div className="hero__content hero__content--centered">
            <hgroup className="hero__heading-group hero__heading-group--centered">
              <HeadingTag className="hero__heading">{heading}</HeadingTag>
            </hgroup>
          </div>
        </div>
      </section>
    );
  }

  // ============================================
  // CENTERED: Centered text + optional bottom image
  // Alert sits above the content container
  // ============================================
  if (variant === 'centered') {
    return (
      <section className={`hero hero--centered`} style={containerStyle}>
        {alertBanner}
        <div className="hero__inner hero__inner--centered">
              <div className="hero__content hero__content--centered">
                <hgroup className="hero__heading-group hero__heading-group--centered">
                  <HeadingTag className="hero__heading">{heading}</HeadingTag>
                  {subheading && (
                    <p className="hero__subheading">{subheading}</p>
                  )}
                </hgroup>
                
                {(primaryCtaText || secondaryCtaText || storeBadges) && (
                  <div className="hero__cta-section hero__cta-section--centered">
                    <div className="hero__cta-group hero__cta-group--centered">
                      {primaryCtaText && (
                        <CtaButton
                          colorVariant={primaryCtaColorVariant || 'primary'}
                          href={primaryCtaHref}
                          id={primaryCtaId}
                          ldAppendix={primaryCtaLdAppendix}
                          behavior={primaryCtaBehavior}
                          action={primaryCtaAction}
                          popupTargetId={primaryCtaTargetPopupId}
                          size={ctaSize}
                        >
                          {primaryCtaText}
                        </CtaButton>
                      )}
                      {secondaryCtaText && (
                        <CtaButton
                          colorVariant={secondaryCtaColorVariant || 'secondary'}
                          href={secondaryCtaHref}
                          id={secondaryCtaId}
                          ldAppendix={secondaryCtaLdAppendix}
                          behavior={secondaryCtaBehavior}
                          action={secondaryCtaAction}
                          popupTargetId={secondaryCtaTargetPopupId}
                          size={ctaSize}
                        >
                          {secondaryCtaText}
                        </CtaButton>
                      )}
                      {storeBadges && <StoreBadgeRow stores={STORE_BADGE_NAMES} />}
                    </div>
                    {(incentivesText || secondaryIncentivesText) && (
                      <div className="hero__below-cta hero__below-cta--centered">
                        {incentivesText && (
                          <div className="hero__incentives hero__incentives--centered">
                            <ReactMarkdown>{incentivesText}</ReactMarkdown>
                          </div>
                        )}
                        {secondaryIncentivesText && (
                          <div className="hero__incentives hero__incentives--centered">
                            <ReactMarkdown>{secondaryIncentivesText}</ReactMarkdown>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Bottom image — inside inner so it inherits the same horizontal padding */}
              {imageSrc && (
                <div className={`hero__bottom-image${videoUrl ? ' hero__bottom-image--video' : ''}`}>
                  {videoUrl ? (
                    <Video
                      posterUrl={imageSrc}
                      posterAlt={imageAlt ?? ''}
                      videoUrl={videoUrl}
                      aspectRatio="16 / 9"
                      playBehavior="inline"
                      playButtonSize="lg"
                    />
                  ) : (
                    <Image
                      url={imageSrc}
                      alt={imageAlt ?? ''}
                      objectFit={imageObjectFit}
                      alignment={imageAlignment}
                      {...overlayProps}
                    />
                  )}
                </div>
              )}
            </div>
      </section>
    );
  }

  // ============================================
  // PRIMARY / SECONDARY: Side-by-side layout
  // Primary: alert above the content container
  // Secondary: alert inside the content area, above eyebrow
  // ============================================
  const contentSection = (
    <div className="hero__content">
      {/* Secondary variant: alert inside content, above eyebrow */}
      {variant === 'secondary' && alertBanner}

      {variant === 'secondary' && eyebrow && (
        <span className="hero__eyebrow">{eyebrow}</span>
      )}
      
      <hgroup className="hero__heading-group">
        <HeadingTag className="hero__heading">{heading}</HeadingTag>
        {subheading && (
          <p className="hero__subheading">{subheading}</p>
        )}
      </hgroup>
      
      {(primaryCtaText || secondaryCtaText || storeBadges || hasVideoCta) && (
        <div className="hero__cta-section">
          <div className="hero__cta-group">
            {primaryCtaText && (
              <CtaButton
                colorVariant={primaryCtaColorVariant || 'primary'}
                href={primaryCtaHref}
                id={primaryCtaId}
                ldAppendix={primaryCtaLdAppendix}
                behavior={primaryCtaBehavior}
                action={primaryCtaAction}
                popupTargetId={primaryCtaTargetPopupId}
                size={ctaSize}
              >
                {primaryCtaText}
              </CtaButton>
            )}

            {hasVideoCta ? (
              <CtaButton
                colorVariant="secondary"
                href={videoUrl}
                action="video"
                size={ctaSize}
              >
                {videoCtaText || 'Watch overview'}
              </CtaButton>
            ) : secondaryCtaText && (
              <CtaButton
                colorVariant={secondaryCtaColorVariant || 'secondary'}
                href={secondaryCtaHref}
                id={secondaryCtaId}
                ldAppendix={secondaryCtaLdAppendix}
                behavior={secondaryCtaBehavior}
                action={secondaryCtaAction}
                popupTargetId={secondaryCtaTargetPopupId}
                size={ctaSize}
              >
                {secondaryCtaText}
              </CtaButton>
            )}
            {storeBadges && <StoreBadgeRow stores={STORE_BADGE_NAMES} />}
          </div>

          {(incentivesText || secondaryIncentivesText) && (
            <div className="hero__below-cta">
              {incentivesText && (
                <div className="hero__incentives">
                  <ReactMarkdown>{incentivesText}</ReactMarkdown>
                </div>
              )}
              {secondaryIncentivesText && (
                <div className="hero__incentives">
                  <ReactMarkdown>{secondaryIncentivesText}</ReactMarkdown>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
  
  const imageSection = imageSrc ? (
    <div className="hero__image-wrapper">
      <Image
        url={imageSrc}
        alt={imageAlt ?? ''}
        objectFit={imageObjectFit}
        alignment={imageAlignment}
        {...overlayProps}
      />
    </div>
  ) : null;
  
  // Two-column layout (or single column if no image — content stays constrained
  // to its natural column width rather than expanding to full width)
  const isImageLeft = imagePosition === 'left';
  
  return (
    <section
      className={`hero hero--${variant}${!imageSection ? ' hero--no-image' : ''} hero--mobile-${mobileStackingMode}`}
      style={containerStyle}
    >
      {/* Primary: alert above content container */}
      {variant === 'primary' && alertBanner}
      <div className="hero__inner">
        {isImageLeft && imageSection && (
          <div className="hero__column">{imageSection}</div>
        )}
        <div className="hero__column">
          {contentSection}
        </div>
        {!isImageLeft && imageSection && (
          <div className="hero__column">{imageSection}</div>
        )}
      </div>
    </section>
  );
};
